import type { GraphConfig, SelectedGraphs } from '../../../SharedComponents/ExportDialog';
import type { GraphData } from '../../../../utils/exportUtils';
import type { PortfolioAnalyticsPropertyResponseDto } from '@zg-rentals/ts-customer-analytics-client';
import { AnalyticsNumBedroomType } from '@zg-rentals/ts-customer-analytics-client';
import type { RentPriceChartData, VacancyRatesChartData, VacantUnitsChartData } from './MarketDataReport.data';
import type {
  CostPerSqFtChartDataPoint,
  PropertyViewCompsChartDataPoint,
  RentCompsChartDataPoint,
} from './Graphs/RentComps/RentComps.types';

export const getMarketDataGraphConfigs = (
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>,
): Array<GraphConfig> => {
  const allSingleFamily = filteredProperties.every((property) => property.aggregateStats.isSingleFamily);
  const isAnyPropertySingleFamily = filteredProperties.some((property) => property.aggregateStats.isSingleFamily);

  const configs: Array<GraphConfig> = [];

  if (!allSingleFamily) {
    configs.push({ id: 'rent-price', label: 'Multifamily rent' });
  }

  if (isAnyPropertySingleFamily) {
    configs.push({ id: 'single-family-rent', label: 'Single-family rent' });
  }

  if (!allSingleFamily) {
    configs.push({ id: 'vacancy-rates', label: 'Vacancy rates' });
    configs.push({ id: 'vacant-units', label: 'Vacant units' });
    configs.push({ id: 'avg-rent-comps', label: 'Average rent comps' });
    configs.push({ id: 'cost-per-sqft-comps', label: 'Cost per sq ft comps' });
    configs.push({ id: 'property-view-comps', label: 'Property view comps' });
  }

  return configs;
};

const extractRentPriceData = (
  chartData: Array<RentPriceChartData>,
  bedsFilter: AnalyticsNumBedroomType = AnalyticsNumBedroomType.ALL,
): GraphData => {
  const data = chartData.map((row) => ({
    Month: row.name,
    'Portfolio Average':
      row['Portfolio average month to date'] !== null
        ? parseFloat((row['Portfolio average month to date'] ?? 0).toFixed(2))
        : parseFloat((row['Portfolio average'] ?? 0).toFixed(2)),
    'Market Average':
      row['Market average month to date'] !== null
        ? parseFloat((row['Market average month to date'] ?? 0).toFixed(2))
        : parseFloat((row['Market average'] ?? 0).toFixed(2)),
  }));

  return {
    name: 'Multifamily_Rent',
    data,
    columns: ['Month', 'Portfolio Average', 'Market Average'],
  };
};

const extractSingleFamilyRentData = (
  chartData: Array<RentPriceChartData>,
  bedsFilter: AnalyticsNumBedroomType = AnalyticsNumBedroomType.ALL,
): GraphData => {
  const data = chartData.map((row) => ({
    Month: row.name,
    'Portfolio Average':
      row['Portfolio average month to date'] !== null
        ? parseFloat((row['Portfolio average month to date'] ?? 0).toFixed(2))
        : parseFloat((row['Portfolio average'] ?? 0).toFixed(2)),
    'Market Average':
      row['Market average month to date'] !== null
        ? parseFloat((row['Market average month to date'] ?? 0).toFixed(2))
        : parseFloat((row['Market average'] ?? 0).toFixed(2)),
  }));

  return {
    name: 'Single_Family_Rent',
    data,
    columns: ['Month', 'Portfolio Average', 'Market Average'],
  };
};

const extractVacancyRatesData = (chartData: Array<VacancyRatesChartData>): GraphData => {
  const data = chartData.map((row) => ({
    Quarter: row.name,
    'Portfolio Average Vacancy Rate (%)': parseFloat(
      (row['Portfolio average quarter to date'] ?? row['Portfolio average'] ?? 0).toFixed(2),
    ),
    'Market Average Vacancy Rate (%)': parseFloat(
      (row['Market average quarter to date'] ?? row['Market average'] ?? 0).toFixed(2),
    ),
  }));

  return {
    name: 'Vacancy_Rates',
    data,
    columns: ['Quarter', 'Portfolio Average Vacancy Rate (%)', 'Market Average Vacancy Rate (%)'],
  };
};

const extractVacantUnitsData = (chartData: Array<VacantUnitsChartData>): GraphData => {
  const data = chartData.map((row) => ({
    Quarter: row.name,
    'Portfolio Vacant Units': parseFloat(
      (row['Portfolio vacant units'] || row['QTD portfolio vacant units']).toFixed(2),
    ),
    'Portfolio Occupied Units': parseFloat(
      (row['Portfolio occupied units'] || row['QTD portfolio occupied units']).toFixed(2),
    ),
    'Market Vacant Units': parseFloat((row['Market vacant units'] || row['QTD market vacant units']).toFixed(2)),
    'Market Occupied Units': parseFloat((row['Market occupied units'] || row['QTD market occupied units']).toFixed(2)),
  }));

  return {
    name: 'Vacant_Units',
    data,
    columns: [
      'Quarter',
      'Portfolio Vacant Units',
      'Portfolio Occupied Units',
      'Market Vacant Units',
      'Market Occupied Units',
    ],
  };
};

const extractRentCompsData = (
  chartData: Array<RentCompsChartDataPoint>,
  allCompNames: Array<string>,
  selectedListingName?: string,
): GraphData => {
  const columns = ['Month', ...(selectedListingName ? [selectedListingName] : []), ...allCompNames, 'Market Average'];
  const data = chartData.map((row) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entry: Record<string, any> = { Month: row.name };
    if (selectedListingName) {
      entry[selectedListingName] =
        (row[`${selectedListingName} month to date`] as number) ?? (row[selectedListingName] as number) ?? 0;
    }
    allCompNames.forEach((name) => {
      entry[name] = (row[`${name} month to date`] as number) ?? (row[name] as number) ?? 0;
    });
    entry['Market Average'] = (row['Market average month to date'] as number) ?? (row['Market average'] as number) ?? 0;
    return entry;
  });

  return { name: 'Average_Rent_Comps', data, columns };
};

const extractCostPerSqFtCompsData = (
  chartData: Array<CostPerSqFtChartDataPoint>,
  allCompNames: Array<string>,
  selectedListingName?: string,
): GraphData => {
  const columns = ['Month', ...(selectedListingName ? [selectedListingName] : []), ...allCompNames];
  const data = chartData.map((row) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entry: Record<string, any> = { Month: row.name };
    if (selectedListingName) {
      entry[selectedListingName] =
        (row[`${selectedListingName} month to date`] as number) ?? (row[selectedListingName] as number) ?? 0;
    }
    allCompNames.forEach((name) => {
      entry[name] = (row[`${name} month to date`] as number) ?? (row[name] as number) ?? 0;
    });
    return entry;
  });

  return { name: 'Cost_Per_SqFt_Comps', data, columns };
};

const extractPropertyViewCompsData = (
  chartData: Array<PropertyViewCompsChartDataPoint>,
  allCompNames: Array<string>,
  selectedListingName?: string,
): GraphData => {
  const columns = ['Month', ...(selectedListingName ? [selectedListingName] : []), ...allCompNames];
  const data = chartData.map((row) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entry: Record<string, any> = { Month: row.name };
    if (selectedListingName) {
      entry[selectedListingName] =
        (row[`${selectedListingName} month to date`] as number) ?? (row[selectedListingName] as number) ?? 0;
    }
    allCompNames.forEach((name) => {
      entry[name] = (row[`${name} month to date`] as number) ?? (row[name] as number) ?? 0;
    });
    return entry;
  });

  return { name: 'Property_View_Comps', data, columns };
};

type PreComputedMarketData = {
  rentPriceChartData: Array<RentPriceChartData>;
  sfRentChartData: Array<RentPriceChartData>;
  vacancyRatesChartData: Array<VacancyRatesChartData>;
  vacantUnitsChartData: Array<VacantUnitsChartData>;
  mfBedsFilter: AnalyticsNumBedroomType;
  sfBedsFilter: AnalyticsNumBedroomType;
  hasSingleFamilyProperties: boolean;
  rentCompsChartData?: Array<RentCompsChartDataPoint>;
  costPerSqFtChartData?: Array<CostPerSqFtChartDataPoint>;
  viewCompsChartData?: Array<PropertyViewCompsChartDataPoint>;
  allCompNames?: Array<string>;
  selectedListingName?: string;
};

export const extractMarketData = (
  selectedGraphs: SelectedGraphs,
  preComputedData: PreComputedMarketData,
): Array<GraphData> => {
  const graphsData: Array<GraphData> = [];

  if (selectedGraphs['rent-price']) {
    graphsData.push(extractRentPriceData(preComputedData.rentPriceChartData, preComputedData.mfBedsFilter));
  }

  if (selectedGraphs['single-family-rent']) {
    graphsData.push(extractSingleFamilyRentData(preComputedData.sfRentChartData, preComputedData.sfBedsFilter));
  }

  if (selectedGraphs['vacancy-rates']) {
    graphsData.push(extractVacancyRatesData(preComputedData.vacancyRatesChartData));
  }

  if (selectedGraphs['vacant-units']) {
    graphsData.push(extractVacantUnitsData(preComputedData.vacantUnitsChartData));
  }

  if (selectedGraphs['avg-rent-comps'] && preComputedData.rentCompsChartData) {
    graphsData.push(
      extractRentCompsData(
        preComputedData.rentCompsChartData,
        preComputedData.allCompNames ?? [],
        preComputedData.selectedListingName,
      ),
    );
  }

  if (selectedGraphs['cost-per-sqft-comps'] && preComputedData.costPerSqFtChartData) {
    graphsData.push(
      extractCostPerSqFtCompsData(
        preComputedData.costPerSqFtChartData,
        preComputedData.allCompNames ?? [],
        preComputedData.selectedListingName,
      ),
    );
  }

  if (selectedGraphs['property-view-comps'] && preComputedData.viewCompsChartData) {
    graphsData.push(
      extractPropertyViewCompsData(
        preComputedData.viewCompsChartData,
        preComputedData.allCompNames ?? [],
        preComputedData.selectedListingName,
      ),
    );
  }

  return graphsData;
};
